package org.matthewo.bracket

import web.cssom.GridLength
import web.cssom.fr


sealed class AllianceSource<out T>
data class AllianceFromMatch<T>(val match: T, val winner: Boolean) : AllianceSource<T>()
data class AllianceSelection(val number: Int) : AllianceSource<Nothing>()

internal fun <A, B> AllianceSource<A>.transform(transform: (match: A, winner: Boolean) -> B) = when (this) {
    is AllianceFromMatch<A> -> AllianceFromMatch(transform(match, winner), winner)
    is AllianceSelection -> this
}

sealed class AllianceTarget<out T>
data class AllianceToMatch<T>(val match: T, val red: Boolean) : AllianceTarget<T>()
data object AllianceWins : AllianceTarget<Nothing>()
data object AllianceEliminated : AllianceTarget<Nothing>()

internal fun <A, B> AllianceTarget<A>.transform(transform: (match: A, red: Boolean) -> B): AllianceTarget<B> = when (this) {
    is AllianceToMatch<A> -> AllianceToMatch(transform(match, red), red)
    AllianceWins -> AllianceWins
    AllianceEliminated -> AllianceEliminated
}

data class PlayoffMatch(
    val upper: Boolean, val round: Int, val number: Int,
) {
    lateinit var redFrom: AllianceSource<PlayoffMatch> internal set
    lateinit var blueFrom: AllianceSource<PlayoffMatch> internal set

    lateinit var winnerTo: AllianceTarget<PlayoffMatch> internal set
    lateinit var loserTo: AllianceTarget<PlayoffMatch> internal set
}

class PlayoffBracketGenerator {
    val matches = mutableMapOf<Int, Match>()

    fun match(upper: Boolean, round: Int, number: Int, block: PlayoffMatchGenerator.() -> Unit): Match {
        val match = PlayoffMatchGenerator(upper, round, number).apply { block() }.generate()
        matches[number] = match
        return match
    }

    fun upper(round: Int, number: Int, block: PlayoffMatchGenerator.() -> Unit) = match(true, round, number, block)
    fun lower(round: Int, number: Int, block: PlayoffMatchGenerator.() -> Unit) = match(false, round, number, block)

    internal fun generate(): List<PlayoffMatch> {
        val playoffMatches = matches.mapValues { (_, match) -> PlayoffMatch(match.upper, match.round, match.number) }

        fun Match.updateSource(redAlliance: Boolean) = (if (redAlliance) red else blue).transform { m, winner ->
            val playoffMatchSource = playoffMatches[m.number]!!
            playoffMatchSource.run { if (winner) ::winnerTo else ::loserTo }.set(AllianceToMatch(playoffMatches[number]!!, redAlliance))
            playoffMatchSource
        }

        matches.forEach { (n, match) ->
            val playoffMatch = playoffMatches[n]!!
            playoffMatch.redFrom = match.updateSource(true)
            playoffMatch.blueFrom = match.updateSource(false)
        }

        playoffMatches.maxBy { it.key }.value.apply {
            winnerTo = AllianceWins
            loserTo = AllianceEliminated
        }

        return playoffMatches.values.sortedBy { it.number }
    }

    class Match(
        val upper: Boolean,
        val round: Int,
        val number: Int,
        val red: AllianceSource<Match>,
        val blue: AllianceSource<Match>,
    )

    internal constructor()

    class PlayoffMatchGenerator(
        val upper: Boolean,
        val round: Int,
        val number: Int,
    ) {
        private var red: AllianceSource<Match>? = null
        private var blue: AllianceSource<Match>? = null

        fun red(match: Match) {
            red = AllianceFromMatch<Match>(match, upper || !match.upper)
        }

        fun red(alliance: Int) {
            red = AllianceSelection(alliance)
        }

        fun blue(match: Match) {
            blue = AllianceFromMatch<Match>(match, upper || !match.upper)
        }

        fun blue(alliance: Int) {
            blue = AllianceSelection(alliance)
        }

        internal fun generate() = Match(
            upper, round, number,
            red ?: error("Red alliance source not set set for match $number"),
            blue ?: error("Blue alliance source not set set for match $number"),
        )
    }
}

fun generateBracket(block: PlayoffBracketGenerator.() -> Unit): List<PlayoffMatch> {
    val generator = PlayoffBracketGenerator()
    generator.block()
    return generator.generate()
}

fun bracket(alliances: Int?) = when (alliances) {
    4 -> bracket4()
    6 -> bracket6()
    8 -> bracket8()
    else -> null
}

fun bracket4() = generateBracket {
    val m1 = upper(1, 1) {
        red(1)
        blue(4)
    }
    val m2 = upper(1, 2) {
        red(2)
        blue(3)
    }

    val m3 = lower(2, 3) {
        red(m1)
        blue(m2)
    }
    val m4 = upper(2, 4) {
        red(m1)
        blue(m2)
    }

    val m5 = lower(3, 5) {
        red(m4)
        blue(m3)
    }

    upper(4, 6) {
        red(m4)
        blue(m5)
    }
}

fun bracket6() = generateBracket {
    val m1 = upper(1, 1) {
        red(4)
        blue(5)
    }
    val m2 = upper(1, 2) {
        red(3)
        blue(6)
    }

    val m3 = upper(2, 3) {
        red(1)
        blue(m1)
    }
    val m4 = upper(2, 4) {
        red(2)
        blue(m2)
    }

    val m5 = lower(3, 5) {
        red(m3)
        blue(m2)
    }
    val m6 = lower(3, 6) {
        red(m4)
        blue(m1)
    }

    val m7 = upper(4, 7) {
        red(m3)
        blue(m4)
    }
    val m8 = lower(4, 8) {
        red(m6)
        blue(m5)
    }

    val m9 = lower(5, 9) {
        red(m7)
        blue(m8)
    }

    upper(6, 10) {
        red(m7)
        blue(m9)
    }
}

fun bracket8() = generateBracket {
    val m1 = upper(1, 1) {
        red(1)
        blue(8)
    }
    val m2 = upper(1, 2) {
        red(4)
        blue(5)
    }
    val m3 = upper(1, 3) {
        red(2)
        blue(7)
    }
    val m4 = upper(1, 4) {
        red(3)
        blue(6)
    }

    val m5 = lower(2, 5) {
        red(m1)
        blue(m2)
    }
    val m6 = lower(2, 6) {
        red(m3)
        blue(m4)
    }
    val m7 = upper(2, 7) {
        red(m1)
        blue(m2)
    }
    val m8 = upper(2, 8) {
        red(m3)
        blue(m4)
    }

    val m9 = lower(3, 9) {
        red(m7)
        blue(m6)
    }
    val m10 = lower(3, 10) {
        red(m8)
        blue(m5)
    }

    val m11 = upper(4, 11) {
        red(m7)
        blue(m8)
    }
    val m12 = lower(4, 12) {
        red(m10)
        blue(m9)
    }

    val m13 = lower(5, 13) {
        red(m11)
        blue(m12)
    }

    upper(6, 14) {
        red(m11)
        blue(m13)
    }
}

fun List<PlayoffMatch>.createRows(): Pair<Dividers<PlayoffMatch>, List<GridLength>> {
    val dividerGenerator = DividerGenerator<PlayoffMatch>()

    val size = 2.5
    val spacing = 0.75

    data class SpaceDividers(val top: Divider, val bottom: Divider)
    data class SegmentDividers(val match: SpaceDividers, val total: SpaceDividers)

    fun createRowDividers(match: PlayoffMatch): SegmentDividers {
        val (matchTop, matchBottom) = dividerGenerator.createArea(match, size)

        val red = match.redFrom
        val totalStart = if (red is AllianceFromMatch && red.winner) {
            val (match, total) = createRowDividers(red.match)
            dividerGenerator.createSpace(matchTop, total.bottom, spacing)
            total.top
        } else matchTop
        val blue = match.blueFrom
        val totalEnd = if (blue is AllianceFromMatch && blue.winner) {
            val (match, total) = createRowDividers(blue.match)
            dividerGenerator.createSpace(total.top, matchBottom, spacing)
            total.bottom
        } else matchBottom
        return SegmentDividers(SpaceDividers(matchTop, matchBottom), SpaceDividers(totalStart, totalEnd))
    }

    val (match, total) = createRowDividers(maxBy { it.number })
    dividerGenerator.createSpace(total.top, -(size - 2 * spacing))
    dividerGenerator.createSpace(total.bottom, size - 2 * spacing)

    val dividers = dividerGenerator.generate()

    return dividers to dividers.spacings.map { it.fr }
}
